import React, { forwardRef } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { cx } from "./cx";

/**
 * The platform button. Reproduces the styles already used across the app (brand green primary,
 * grey secondary, outlined, ghost, danger) so screens keep their look while sharing one component.
 *
 * Labels wrap instead of overflowing, every size meets the 44px touch target on touch screens, and
 * `block` makes a button full-width (the usual choice on phones).
 *
 * Renders a <button> by default, a router <Link> when `to` is set, or an <a> when `href` is set.
 */
export const BUTTON_VARIANTS = {
  primary:
    "bg-brand text-white shadow-sm hover:bg-brand-hover focus-visible:ring-brand/40 disabled:hover:bg-brand",
  secondary:
    "bg-gray-100 text-gray-700 hover:bg-gray-200 focus-visible:ring-gray-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600",
  outline:
    "border border-brand text-brand bg-white hover:bg-brand-soft focus-visible:ring-brand/40 dark:bg-transparent dark:hover:bg-brand/10",
  subtle:
    "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 focus-visible:ring-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700",
  ghost:
    "text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-300 dark:text-slate-200 dark:hover:bg-slate-800",
  danger:
    "bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-300",
  "danger-outline":
    "border border-red-300 text-red-700 bg-white hover:bg-red-50 focus-visible:ring-red-200 dark:bg-transparent dark:text-red-300 dark:border-red-500/60 dark:hover:bg-red-500/10",
};

export const BUTTON_SIZES = {
  xs: "min-h-8 px-2.5 py-1 text-xs gap-1 rounded-md",
  sm: "min-h-9 px-3 py-1.5 text-sm gap-1.5 rounded-lg",
  md: "min-h-10 px-4 py-2 text-sm gap-2 rounded-lg",
  lg: "min-h-12 px-6 py-3 text-base gap-2 rounded-xl",
};

const ICON_ONLY_SIZES = {
  xs: "size-8 p-0 rounded-md",
  sm: "size-9 p-0 rounded-lg",
  md: "size-10 p-0 rounded-lg",
  lg: "size-12 p-0 rounded-xl",
};

const Button = forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    pill = false,
    block = false,
    loading = false,
    disabled = false,
    iconOnly = false,
    leadingIcon = null,
    trailingIcon = null,
    to,
    href,
    type = "button",
    className,
    children,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;
  const classes = cx(
    "inline-flex items-center justify-center text-center font-semibold leading-snug select-none",
    "transition-[background-color,color,box-shadow,transform] duration-150 ease-out",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
    "active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100",
    // Long Arabic/Chinese labels wrap instead of spilling out of the box.
    "whitespace-normal break-words [overflow-wrap:anywhere]",
    // Comfortable to tap on touch screens regardless of size.
    "[@media(pointer:coarse)]:min-h-11",
    iconOnly ? ICON_ONLY_SIZES[size] || ICON_ONLY_SIZES.md : BUTTON_SIZES[size] || BUTTON_SIZES.md,
    BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary,
    pill && "rounded-full",
    block && "flex w-full",
    className
  );

  const content = (
    <>
      {loading ? (
        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        leadingIcon && <span className="inline-flex shrink-0" aria-hidden="true">{leadingIcon}</span>
      )}
      {children != null && <span className={cx("min-w-0", iconOnly && "sr-only")}>{children}</span>}
      {trailingIcon && !loading && (
        <span className="inline-flex shrink-0" aria-hidden="true">{trailingIcon}</span>
      )}
    </>
  );

  if (to && !isDisabled) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  if (href && !isDisabled) {
    return (
      <a ref={ref} href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
});

export default Button;
